import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Metadata from '../../components/Metadata/Metadata';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Card from '../../components/Card/Card';
import { Box, Typography } from '@mui/material';
import Section from '../../components/Section/Section';
import Accordion from '../../components/Accordion/Accordion';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Innovation at General Genomics: Transforming Industries"
        description="Discover General Genomics’ cutting-edge advancements in bioscience, AI, and data solutions. Explore innovations like Curo46®, Synthetic Data (SYN46™), and De-Identified Data."
      />
      <Hero
        helper="Innovation"
        headline="Driving breakthroughs with AI, synthetic data (SYN46™), and custom solutions"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Revolutionize Your Industry
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics is transforming bioscience, AI, and data
          solutions through innovation. Explore groundbreaking tools like
          Curo46®, SYN46™, and De-Identified Data, each designed to solve your
          industry’s biggest challenges. By leveraging advanced technology and
          data-driven insights, we empower organizations to innovate with
          confidence, achieve new efficiencies, and unlock the full potential of
          their data. Contact us today to see how our cutting-edge solutions can
          help your organization stay ahead.
        </p>
        <Image
          path="/images/library/office-group-3.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit image="/images/library/lab-woman-10.webp" position="right">
        <p className="helper action-on-view action-fade-in">Why Choose Us</p>
        <h2 className="action-on-view action-fade-in">
          Unparalleled Innovation at Your Fingertips
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Curo46® Platform:</strong> Harness AI-driven insights to
            streamline operations and drive impactful decisions across your
            organization.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Synthetic Data (SYN46™):</strong> Create secure,
            privacy-compliant datasets that enable robust analytics and model
            training without compromising sensitive information.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Custom Data Solutions:</strong> Tailor-made tools to align
            with your specific business needs, ensuring optimized performance
            and efficiency.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>De-Identified Data:</strong> Safeguard data privacy while
            maximizing usability for advanced research and analytics.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Expertise:</strong> Leverage our deep R&D
            capabilities and domain knowledge to stay ahead of the competition.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More of General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover related resources that expand on our AI-powered platforms,
          data innovation, and service capabilities.
        </p>
        <div className="card-group">
          <Card
            icon="hub"
            headline="Curo46® Platform"
            subhead="Learn about our AI-powered platform that transforms genomics with cutting-edge technology."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Synthetic Data (SYN46™)"
            subhead="Explore the creation and applications of synthetic data (SYN46™) for enhanced privacy and analysis."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="engineering"
            headline="Our Capabilities"
            subhead="Dive into our service offerings that connect innovation with real-world solutions."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="cloud"
            headline="Data Portal"
            subhead="Link innovation with practical applications by accessing our robust data portal for clients."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/lab-technicians-man-laptop-2.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; innovations
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Find answers about General Genomics’ cutting-edge technologies,
          including Curo46®, Synthetic Data (SYN46™), and Custom Data
          Solutions.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is Curo46® and how can it benefit my business?"
          >
            <Link to="/innovation/curo46">Curo46®</Link> is General
            Genomics&apos; AI-powered platform designed to drive innovation in
            bioscience and data solutions. It helps businesses optimize
            processes with advanced data analytics, improving efficiency and
            outcomes. To learn more about its impact, explore our detailed page
            on <Link to="/innovation/curo46">Curo46®</Link>.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics use Synthetic Data (SYN46™) in its solutions?"
          >
            Synthetic Data (SYN46™) from General Genomics enables
            privacy-focused data creation, allowing businesses to leverage
            high-quality data while maintaining security. Discover how Synthetic
            Data (SYN46™) can support your organization by visiting our
            dedicated page for more insights.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What are Custom Data Solutions and how do they align with my needs?"
          >
            General Genomics offers tailored Custom Data Solutions that cater to
            specific business challenges, enhancing decision-making with
            precision data. Explore how these solutions can be customized for
            your organization on our capabilities page.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I explore General Genomics’ full range of innovative offerings?"
          >
            You can easily access more information about our technologies,
            including <Link to="/innovation/curo46">Curo46®</Link>, Synthetic
            Data, and Custom Data Solutions, through the links provided on our
            Innovation landing page. Feel free to reach out for a demo or more
            details.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I get in touch with General Genomics for further assistance?"
          >
            For more information, please contact us directly using the{' '}
            <Link to="/contact-us">“Contact Us”</Link> link on our Innovation
            page. We’re happy to assist you with any inquiries.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
